import { useCallback } from 'react';
import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';

type ByoSettingOutput = components['schemas']['ByoSettingOutput'];
export type ByoSettingInput = components['schemas']['ByoSettingInput'];

type ByoSettingsResponse = ByoSettingOutput[];

const BYO_SETTINGS_ENDPOINT: keyof paths = '/ByoSettings';

export interface UseByoSettingsReturn {
    data: ByoSettingsResponse | null;
    isLoading: boolean;
    error: string;
    deleteSetting: (id: number) => Promise<void>;
    createSetting: (data: ByoSettingInput) => Promise<number>;
    updateSetting: (data: ByoSettingInput) => Promise<void>;
    refetch: () => Promise<void>;
}

export default function useByoSettings(): UseByoSettingsReturn {
    const { 
        data = [], 
        isLoading: isFetching, 
        error, 
        doFetch: reFetch
    } = useAxios<ByoSettingsResponse>({
        method: 'get',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const { 
        isLoading: isDeleting, 
        doFetch: deleteRequest 
    } = useAxios({
        method: 'delete',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const { 
        isLoading: isCreating, 
        doFetch: createRequest 
    } = useAxios<number, ByoSettingInput>({
        method: 'post',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const { 
        isLoading: isUpdating, 
        doFetch: updateRequest 
    } = useAxios<boolean, ByoSettingInput>({
        method: 'put',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const isLoading = isFetching || isDeleting || isCreating || isUpdating;

    const deleteSetting = useCallback(
        async (id: number) => {
            await deleteRequest({
                url: `${BYO_SETTINGS_ENDPOINT}/${id}`,
            });
            await reFetch();
        },
        [deleteRequest, reFetch]
    );

    const createSetting = useCallback(
        async (settingData: ByoSettingInput) => {
            const response = await createRequest({ data: settingData });
            return response?.data ?? 0;
        },
        [createRequest]
    );

    const updateSetting = useCallback(
        async (settingData: ByoSettingInput) => {
            await updateRequest({ data: settingData });
            await reFetch();
        },
        [updateRequest, reFetch]
    );

    const refetch = useCallback(
        async () => {
            await reFetch();
        },
        [reFetch]
    );

    return {
        data,
        isLoading,
        error,
        deleteSetting,
        createSetting,
        updateSetting,
        refetch,
    };
}
